import { type ReactNode, useMemo, useRef, useState } from 'react';
import {
  type FieldPath,
  type FieldValues,
  get,
  useController,
  useFormContext,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { type Language, type LocalizedText, zLocalizedText } from '@/shared/api';
import { cn } from '@/shared/lib';
import { GLYPH } from '../glyphs';
import { Input } from '../Input';
import { SegmentedControl } from '../SegmentedControl';
import { useContentLanguages } from './contentLanguages';
import { FormField } from './FormField';
import { useRegisterAliases, useRegisterFields } from './fieldRegistry';
import { localizedTextOf } from './localizedText';
import { emptyPairRow, type PairParts, plainPairRow } from './pairs';
import { isLanguageOf, nestedErrorMessage } from './useLocalizedInput';

export interface PairsFieldProps<TValues extends FieldValues> {
  /** Поле контракта (`details`, `steps`, `route`…): ошибки сервера `поле.N.часть[.язык]` — под строку. */
  name: FieldPath<TValues>;
  label: string;
  hint?: string;
  /** Части строки в контракте: `['label', 'value']` или `['title', 'text']`. */
  parts: PairParts;
  /** Нумерованные шаги (кружок с номером слева). */
  numbered?: boolean;
  placeholders?: readonly [string, string];
  /** «+ Добавить строку» / «+ Добавить шаг» / «+ Добавить пункт» / «+ Добавить день». */
  addLabel: string;
  maxA?: number;
  maxB?: number;
  /** Наибольшее число строк (у сервера — 20). */
  maxRows?: number;
  required?: boolean;
  disabled?: boolean;
  /**
   * Строки без языка (`PlainPairRow`: части — обычные строки, переключателя языка нет) — значения
   * настроек интеграции (`key_value`).
   */
  plain?: boolean;
  /**
   * Набор строк задан (ключи — из контракта): первая часть — подпись, а не ввод; строки не
   * добавляются и не удаляются.
   */
  fixedRows?: boolean;
  /** Элемент справа от подписи (точка изменённого поля), если нет переключателя языка. */
  aside?: ReactNode;
}

/** Часть строки: LocalizedText или (в режиме `plain`) строка. */
type Cell = LocalizedText | string;

interface Row {
  key: string;
  a: Cell;
  b: Cell;
}

function parseCell(value: unknown, plain: boolean): Cell | null {
  if (plain) return typeof value === 'string' ? value : null;
  const parsed = zLocalizedText.safeParse(value);
  return parsed.success ? parsed.data : null;
}

const rowSchema = (value: unknown, plain: boolean): Row[] => {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item: unknown) => {
    if (typeof item !== 'object' || item === null) return [];
    const key = 'key' in item && typeof item.key === 'string' ? item.key : '';
    const a = parseCell('a' in item ? item.a : null, plain);
    const b = parseCell('b' in item ? item.b : null, plain);
    return key && a !== null && b !== null ? [{ key, a, b }] : [];
  });
};

function cellText(cell: Cell, language: Language): string {
  return typeof cell === 'string' ? cell : localizedTextOf(cell, language);
}

function withText(value: Cell, language: Language, text: string): Cell {
  return typeof value === 'string' ? text : { ...value, [language]: text };
}

function rowMessage(error: unknown, languages: readonly Language[], multilingual: boolean) {
  if (typeof error !== 'object' || error === null) return undefined;
  if ('message' in error && typeof error.message === 'string' && error.message) {
    return error.message;
  }
  for (const language of languages) {
    const message = nestedErrorMessage(error, language);
    if (message) return multilingual ? `${language.toUpperCase()}: ${message}` : message;
  }
  return undefined;
}

/**
 * Поле «пары» формы (прототип `pairs`: строки «название — значение» или нумерованные шаги): два
 * ввода 34 (1 : 2), «×» убирает строку, «+ Добавить …» — новую в конце. Значение — строки
 * `{key, a, b}` с LocalizedText; язык ввода (World) — один переключатель на поле. Ошибки сервера по
 * частям строки (`details.0.label.ru`) ложатся под строку. `plain` — части-строки без языка,
 * `fixedRows` — заданный набор ключей (первая часть — подпись, строки не добавляются и не удаляются).
 */
export function PairsField<TValues extends FieldValues>({
  name,
  label,
  hint,
  parts,
  numbered = false,
  placeholders,
  addLabel,
  maxA,
  maxB,
  maxRows = 20,
  required,
  disabled,
  plain = false,
  fixedRows = false,
  aside,
}: PairsFieldProps<TValues>) {
  const { t } = useTranslation('form');
  const { languages } = useContentLanguages();
  const multilingual = !plain && languages.length > 1;
  const [active, setActive] = useState<Language>(languages[0] ?? 'ru');
  const language = languages.includes(active) ? active : (languages[0] ?? active);
  const { control, formState } = useFormContext<TValues>();
  const { field, fieldState } = useController({ name, control });
  const rows = rowSchema(field.value, plain);

  const names = useMemo(
    () => [name, ...rows.flatMap((_, index) => [`${name}.${index}.a`, `${name}.${index}.b`])],
    [name, rows],
  );
  useRegisterFields(names);
  const aliases = useMemo(() => {
    const result: Record<string, string> = {};
    rows.forEach((_, index) => {
      parts.forEach((part, partIndex) => {
        const target = `${name}.${index}.${partIndex === 0 ? 'a' : 'b'}`;
        result[`${name}.${index}.${part}`] = target;
        for (const code of languages) result[`${name}.${index}.${part}.${code}`] = target;
      });
    });
    return result;
  }, [name, rows, parts, languages]);
  useRegisterAliases(aliases);

  const update = (next: Row[]) => field.onChange(next);
  // Новая строка получает фокус: ввод продолжается в ней, а не в прежнем поле.
  const focusKey = useRef<string | null>(null);
  const focusNew = (key: string) => (node: HTMLInputElement | null) => {
    if (node && focusKey.current === key) {
      focusKey.current = null;
      node.focus();
    }
  };
  const listError = fieldState.error?.message;
  const isDisabled = disabled || field.disabled;

  return (
    <FormField
      label={label}
      required={required}
      error={listError}
      hint={hint}
      wide
      aside={
        multilingual ? (
          <SegmentedControl
            variant="compact"
            aria-label={t('localized.inputLanguage')}
            value={language}
            onValueChange={(next) => {
              if (isLanguageOf(languages, next)) setActive(next);
            }}
            items={languages.map((code) => ({ value: code, label: t(`localized.short.${code}`) }))}
          />
        ) : (
          aside
        )
      }
    >
      <div className="flex flex-col gap-8">
        {rows.map((row, index) => {
          const errorA = rowMessage(
            get(formState.errors, `${name}.${index}.a`),
            languages,
            multilingual,
          );
          const errorB = rowMessage(
            get(formState.errors, `${name}.${index}.b`),
            languages,
            multilingual,
          );
          const position = index + 1;
          return (
            <div key={row.key} className="flex flex-col gap-4">
              <div className="flex items-center gap-8">
                {numbered ? (
                  <span
                    aria-hidden="true"
                    className="flex size-26 shrink-0 items-center justify-center rounded-13 bg-surface-sunken text-[12px] font-medium text-wine-bordeaux tabular"
                  >
                    {position}
                  </span>
                ) : null}
                {fixedRows ? (
                  <span className="min-w-0 flex-[1_1_0] truncate text-[13px] font-medium text-ink-900">
                    {cellText(row.a, language)}
                  </span>
                ) : (
                  <Input
                    ref={focusNew(row.key)}
                    value={cellText(row.a, language)}
                    lang={plain ? undefined : language}
                    placeholder={placeholders?.[0]}
                    maxLength={maxA}
                    disabled={isDisabled}
                    aria-label={t('pairs.partA', { label, n: position })}
                    aria-invalid={errorA ? true : undefined}
                    onChange={(event) =>
                      update(
                        rows.map((item) =>
                          item.key === row.key
                            ? { ...item, a: withText(item.a, language, event.target.value) }
                            : item,
                        ),
                      )
                    }
                    onBlur={field.onBlur}
                    className="min-w-0 flex-[1_1_0] font-medium"
                  />
                )}
                <Input
                  value={cellText(row.b, language)}
                  lang={plain ? undefined : language}
                  placeholder={placeholders?.[1]}
                  maxLength={maxB}
                  disabled={isDisabled}
                  aria-label={
                    fixedRows
                      ? t('pairs.valueOf', { label, key: cellText(row.a, language) })
                      : t('pairs.partB', { label, n: position })
                  }
                  aria-invalid={errorB ? true : undefined}
                  onChange={(event) =>
                    update(
                      rows.map((item) =>
                        item.key === row.key
                          ? { ...item, b: withText(item.b, language, event.target.value) }
                          : item,
                      ),
                    )
                  }
                  onBlur={field.onBlur}
                  className="min-w-0 flex-[2_1_0]"
                />
                {fixedRows ? null : (
                  <button
                    type="button"
                    disabled={isDisabled}
                    title={t('pairs.remove')}
                    aria-label={t('pairs.removeRow', { label, n: position })}
                    onClick={() => update(rows.filter((item) => item.key !== row.key))}
                    className="w-16 shrink-0 cursor-pointer border-0 bg-transparent p-0 text-center text-[15px] leading-[18px] text-ink-400 hover:text-status-error disabled:cursor-not-allowed"
                  >
                    {GLYPH.times}
                  </button>
                )}
              </div>
              {errorA || errorB ? (
                <span
                  role="alert"
                  className={cn(
                    'text-[11px] leading-[16px] text-status-error',
                    numbered ? 'pl-34' : undefined,
                  )}
                >
                  {[errorA, errorB].filter(Boolean).join(' ')}
                </span>
              ) : null}
            </div>
          );
        })}
        {rows.length < maxRows && !isDisabled && !fixedRows ? (
          <button
            type="button"
            onClick={() => {
              const row = plain ? plainPairRow() : emptyPairRow(languages);
              focusKey.current = row.key;
              update([...rows, row]);
            }}
            className="cursor-pointer self-start border-0 bg-transparent p-0 text-[12px] font-medium text-wine-bordeaux hover:underline"
          >
            {addLabel}
          </button>
        ) : null}
      </div>
    </FormField>
  );
}
